<template>
  <section
    class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#202F7E] via-[#27306f] to-[#4D2542] text-white shadow-sm"
  >
    <div class="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-[#F4F3E3]/10 blur-2xl"></div>
    <div class="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-[#4D2542]/40 blur-3xl"></div>

    <div class="relative px-7 py-9 sm:px-10 sm:py-12">
      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">Course</p>
      <h1 class="mt-2 max-w-3xl text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
        {{ course.name }}
      </h1>
      <p class="mt-3 max-w-2xl text-[15px] leading-relaxed text-white/80">{{ course.description }}</p>

      <div class="mt-6 max-w-xs">
        <CourseProgress :completed="completedLectures" :total="totalLectures" tone="dark" />
      </div>

      <div class="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
        <RouterLink
          v-if="ctaLecture"
          :to="{ name: 'University Lecture', params: { course_slug: course.slug, order: ctaLecture.order } }"
          class="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-[#202F7E] transition-colors hover:bg-[#F4F3E3]"
        >
          <vue-feather :type="ctaIcon" size="16" />
          {{ ctaLabel }}
        </RouterLink>
        <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-white/80">
          <span class="flex items-center gap-2">
            <vue-feather type="layers" size="16" />
            {{ course.course_sections.length }} modules
          </span>
          <span class="flex items-center gap-2">
            <vue-feather type="play-circle" size="16" />
            {{ totalLectures }} lessons
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CourseProgress from '@/components/agent/university/CourseProgress.vue';
import type { CourseWithSections } from '@/api/agent/university/course';

const props = defineProps<{ course: CourseWithSections }>();

const allLectures = computed(() => props.course.course_sections.flatMap(section => section.lectures));

const totalLectures = computed(() => allLectures.value.length);

const completedLectures = computed(() => allLectures.value.filter(lecture => lecture.completed).length);

const firstLecture = computed(() => allLectures.value[0] ?? null);

const nextLecture = computed(() => allLectures.value.find(lecture => !lecture.completed) ?? null);

const allComplete = computed(() => totalLectures.value > 0 && completedLectures.value === totalLectures.value);

const ctaLecture = computed(() => (allComplete.value ? firstLecture.value : (nextLecture.value ?? firstLecture.value)));

const ctaLabel = computed(() => {
  if (allComplete.value) return 'Review course';
  if (completedLectures.value > 0) return 'Resume';
  return 'Start course';
});

const ctaIcon = computed(() => (allComplete.value ? 'rotate-ccw' : 'play'));
</script>
